import dayjs from 'dayjs'

import { DAY_TO_NUMBER } from '@/types/offer'
import type { LoyaltyReward, OfferAvailabilitySlot } from '@/types/types'
import type { ActiveDay, DistributionChannel, TimeSlot } from '@/types/offer'
import { slugBelongsTo } from '@/components/rewards/utils/rewardTypeRegistry'
import { parseRewardExternalIdCsv } from '@/components/rewards/utils/rewardHelpers'

export type PromotionEngineChannel =
  | 'pos'
  | 'web'
  | 'touchscreen'
  | 'kiosk'
  | 'online'
  | 'click_and_collect'
  | 'delivery'

export const mapDistributionChannelToApi = (channel: DistributionChannel): PromotionEngineChannel => {
  if (channel === 'kiosk') return 'touchscreen'
  if (channel === 'online' || channel === 'click_collect' || channel === 'delivery') return 'web'
  return 'pos'
}

export const mapApiChannelToUi = (channel: string): DistributionChannel[] => {
  const normalized = channel.toLowerCase()
  if (normalized === 'web') return ['online']
  if (normalized === 'touchscreen') return ['kiosk']
  if (normalized === 'click_and_collect') return ['click_collect']
  if (normalized === 'kiosk') return ['kiosk']
  if (normalized === 'pos') return ['in_store']
  if (normalized === 'online') return ['online']
  if (normalized === 'delivery') return ['delivery']
  return []
}

export const getOfferStatus = (
  startDate: string | null | undefined,
  endDate: string | null | undefined
): 'active' | 'scheduled' | 'expired' => {
  const now = dayjs()
  const start = startDate ? dayjs(startDate) : null
  const end = endDate ? dayjs(endDate) : null

  if (end && !end.isAfter(now)) return 'expired'
  if (start && start.isAfter(now)) return 'scheduled'
  return 'active'
}

export const toIsoDate = (dateStr: string, endOfDay = false): string => {
  const match = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})$/)
  if (!match) throw new Error(`Invalid date format: "${dateStr}". Expected YYYY-MM-DD`)
  const [, y, m, d] = match.map(Number)
  const date = new Date(
    Date.UTC(y, m - 1, d, endOfDay ? 23 : 0, endOfDay ? 59 : 0, endOfDay ? 59 : 0, endOfDay ? 999 : 0)
  )
  return date.toISOString()
}

export const fromIsoToInputDate = (isoDate: string | null | undefined): string | null => {
  if (!isoDate) return null
  return dayjs(isoDate).format('YYYY-MM-DD')
}

export const getSampleDateForDay = (day: ActiveDay): Date => {
  const base = new Date(2024, 0, 1)
  const date = new Date(base)
  date.setDate(base.getDate() + DAY_TO_NUMBER[day] - 1)
  return date
}

export const CHANNEL_LABEL_KEY: Record<DistributionChannel, string> = {
  in_store: 'pages.offer.reward.channelInStore',
  kiosk: 'pages.offer.reward.channelKiosk',
  click_collect: 'pages.offer.reward.channelClickCollect',
  online: 'pages.offer.reward.channelOnline',
  delivery: 'pages.offer.reward.channelDelivery'
}

export const applyOfferProductDiscountRewardTypeWhenSkusSelected = (
  payload: { rewardTypeId?: number | null; externalId?: string | null; items?: unknown[] | null },
  currentSlug: string | undefined,
  rewardTypeIdsBySlug: Record<string, number> | null | undefined
): void => {
  if (!rewardTypeIdsBySlug || !currentSlug) return
  const hasProductTargets =
    parseRewardExternalIdCsv(payload.externalId).length > 0 ||
    (Array.isArray(payload.items) && payload.items.length > 0)
  if (!hasProductTargets) return
  if (slugBelongsTo('percentDiscount', currentSlug) && rewardTypeIdsBySlug.product_discount_percent != null) {
    payload.rewardTypeId = rewardTypeIdsBySlug.product_discount_percent
  } else if (
    slugBelongsTo('discount', currentSlug) &&
    !slugBelongsTo('percentDiscount', currentSlug) &&
    rewardTypeIdsBySlug.product_discount_amount != null
  ) {
    payload.rewardTypeId = rewardTypeIdsBySlug.product_discount_amount
  }
}

export const switchOfferDiscountRewardTypeForScope = (
  reward: LoyaltyReward,
  nextScope: 'cart' | 'products',
  rewardTypeIdsBySlug: Record<string, number> | null | undefined
): boolean => {
  if (!rewardTypeIdsBySlug) {
    reward.applyReductionOn = nextScope
    return false
  }
  const currentSlug = reward.rewardType?.slug
  if (!slugBelongsTo('discount', currentSlug)) {
    reward.applyReductionOn = nextScope
    return false
  }

  const bySlug = (slug: string) => rewardTypeIdsBySlug[slug]
  const isPercent = slugBelongsTo('percentDiscount', currentSlug)
  const targetSlug =
    nextScope === 'products'
      ? isPercent
        ? 'product_discount_percent'
        : 'product_discount_amount'
      : isPercent
        ? 'voucher_discount_percent'
        : 'voucher_discount_amount'
  const targetTypeId = bySlug(targetSlug)
  if (targetTypeId == null) {
    reward.applyReductionOn = nextScope
    return false
  }

  reward.rewardTypeId = targetTypeId
  if (reward.rewardType) {
    reward.rewardType.id = targetTypeId
    reward.rewardType.slug = targetSlug
  }
  reward.applyReductionOn = nextScope

  if (nextScope === 'cart') {
    reward.items = []
    reward.externalId = ''
  }

  return true
}

export const buildAvailabilitySlotsFromTimeSlots = (timeSlots: TimeSlot[]): OfferAvailabilitySlot[] => {
  return timeSlots
    .map((slot) => {
      if (!slot.days.length) return null
      const daysOfWeek = slot.days.map((d: ActiveDay) => DAY_TO_NUMBER[d]).sort((a, b) => a - b)
      const timeRanges = slot.startTime && slot.endTime ? [{ start: slot.startTime, end: slot.endTime }] : []
      return { daysOfWeek, timeRanges }
    })
    .filter((slot): slot is OfferAvailabilitySlot => !!slot)
}
